import { useCreditAccountByIdQuery } from "@/gqlhooks/profile/useCreditAccountById";
import { CreditAccountScreen } from "./CreditAccount.screen";
import { useAuthState } from "@/store/auth";
import type {
  CreditAccountTransaction} from "@/__generated__/graphql";
import {
  CreditTypeEnum,
} from "@/__generated__/graphql";
import type { CreditAccountType } from "@/types/profile";
import { CreditAccountTopUpContainer } from "./credit-account-topup/CreditAccountTopUp.container";
import { getPlatform } from "@/lib/utils";
import { useRoute } from "@/components/shared/router";
import { useProfileState } from "@/store/profile";
import { useEffect } from "react";
import { showToast } from "@/components/ui/toast/show-toast";
import {
  getDepartmentCreditLabel,
  getDepartmentLinkedName,
  isDepartmentCreditType,
} from "./utils";

export interface CreditOverviewCardProps {
  type: Exclude<CreditAccountType, "overview" | "general">;
  balance: string;
}

export type CreditAccountProps = {
  type: CreditAccountType;
  title: string;
  balance: string;
  transactions: CreditAccountTransaction[];
  canTopUp: boolean;
  canShowExpiryList: boolean;
  expiryType: Extract<CreditAccountType, "massage" | "beauty" | "pet"> | null;
  isEmptyBalance: boolean;
  departmentLabel: string;
  onClickCard?(): void;
  onViewMore(): void;
  loading: boolean;
};

export const CreditAccountContainer = ({
  type,
}: {
  type: CreditAccountType;
}) => {
  const generalType = type === "general";
  const departmentLinkedName = getDepartmentLinkedName(type);
  const title = getDepartmentCreditLabel(type);
  const departmentLabel = getDepartmentCreditLabel(type);

  const { push, replace } = useRoute();
  const isWeb = getPlatform() === "web";
  const { setCreditTopUpModal, setCreditAccountType } = useProfileState();
  const canTopUp = type !== "general" && type !== "overview";
  const canShowExpiryList = isDepartmentCreditType(type);
  const expiryType = isDepartmentCreditType(type) ? type : null;

  useEffect(() => {
    setCreditAccountType(type);
  }, [setCreditAccountType, type]);

  const creditAccountId = useAuthState((state) =>
    state.data.userInfo.creditAccounts.find((credit) => {
      if (generalType) {
        return credit.creditType === CreditTypeEnum.General;
      }

      return (
        credit.creditType === CreditTypeEnum.Department &&
        credit.linkedObject?.linkedType === "Department" &&
        credit.linkedObject.name === departmentLinkedName
      );
    }),
  )?.id;

  useEffect(() => {
    if (!creditAccountId) {
      showToast({
        title: "Credit account not found for selected type",
        type: "error",
      });
      replace({ pageKey: "profile" });
    }
  }, [creditAccountId]);

  const { data, transactions, loading } = useCreditAccountByIdQuery(
    creditAccountId ?? "",
  );
  const balance = String(data?.creditAccountById.balance ?? 0);
  const isEmptyBalance = Number(balance) === 0;

  const handleClickTopUp = () => {
    if (isWeb) {
      setCreditTopUpModal(true);
    } else {
      push({
        pageKey: "creditAccountTopup",
      });
    }
  };

  const handleClickViewMore = () => {
    push({
      pageKey: "creditAccountTransaction",
    });
  };

  if (!creditAccountId) {
    return null;
  }

  return (
    <>
      <CreditAccountScreen
        title={title}
        loading={loading}
        balance={balance}
        transactions={transactions.slice(0, 3)}
        canTopUp={canTopUp}
        canShowExpiryList={canShowExpiryList}
        expiryType={expiryType}
        isEmptyBalance={isEmptyBalance}
        departmentLabel={departmentLabel}
        onClickCard={handleClickTopUp}
        type={type}
        onViewMore={handleClickViewMore}
      />
      {isWeb && <CreditAccountTopUpContainer />}
    </>
  );
};
